import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ResultReviewItem from './ResultReviewItem';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { SyntheticEvent } from 'react';
import { SnackbarCloseReason } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import RareButton from '../buttons/RareButton';
import {
  InterpretationEnum,
  ChangeActionSelected,
  ReviewState,
} from '../CustomHooks';
import api from '../../../redux/features/auth/axiosAuthHelper';

interface Props {
  resultIds: string[];
  handleBackButton: () => void;
  onUpdated: () => void;
}

const ResultsReviewCard: React.FC<Props> = ({
  handleBackButton,
  onUpdated,
  resultIds,
}) => {
  const authData = useSelector((state: RootState) => state.auth);
  const [reviewData, setReviewData] = useState<ReviewState>({});
  const [changeActionSelected, setChangeActionSelected] =
    useState<ChangeActionSelected>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(
    null
  );
  const [hasReachedLastPage, setHasReachedLastPage] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showBackDialog, setShowBackDialog] = useState(false);

  const [windowStart, setWindowStart] = useState(0);

  const windowSize = 20;

  const windowEnd = Math.min(windowStart + windowSize, resultIds.length);
  const totalPages = Math.ceil(resultIds.length / windowSize);
  const currentPage = Math.floor(windowStart / windowSize) + 1;
  const isLastPage = windowEnd >= resultIds.length;

  // Check if user has reached last page
  React.useEffect(() => {
    if (isLastPage && !hasReachedLastPage) {
      setHasReachedLastPage(true);
    }
  }, [isLastPage, hasReachedLastPage]);

  // The IDs visible on the current page
  const currentWindowIds = useMemo(
    () => resultIds.slice(windowStart, windowEnd),
    [resultIds, windowStart, windowEnd]
  );

  // How many of the visible items have been explicitly reviewed (confirm/change)
  const reviewedInCurrentWindow = useMemo(
    () => currentWindowIds.reduce((acc, id) => acc + (reviewData[id] ? 1 : 0), 0),
    [currentWindowIds, reviewData]
  );

  // Count everything before this page as already reviewed
  const baselineFromPrevPages = windowStart;

  const reviewedCount = Math.min(
    baselineFromPrevPages + reviewedInCurrentWindow,
    resultIds.length
  );

  const handlePrevious = () =>
    setWindowStart((prev) => Math.max(prev - windowSize, 0));

  const handleNext = () =>
    setWindowStart((prev) =>
      prev + windowSize < resultIds.length ? prev + windowSize : prev
    );

  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // fallback in case the container isn't mounted for some reason
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [windowStart]);

  const handleUpdateReview = (
    resultId: string,
    updates: { interpretation: InterpretationEnum; notes: string }
  ) => {
    setReviewData((prev) => ({
      ...prev,
      [resultId]: {
        interpretation: updates.interpretation,
        notes: updates.notes,
      },
    }));
  };

  const handleUpdateActionConfirm = (resultId: string, update: boolean) => {
    setChangeActionSelected((prev) => ({
      ...prev,
      [resultId]: update,
    }));
  };

  const handleSaveClick = () => {
    setShowSaveDialog(true);
  };

  const handleConfirmSave = async () => {
    setShowSaveDialog(false);
    try {
      setSaving(true);
      setSaveStatus(null);
      await api.patch(
        'results/bulk/review-results',
        { reviews: reviewData, result_ids: resultIds },
        {
          headers: {
            Authorization: `Bearer ${authData.access}`,
          },
        }
      );
      setSaveStatus('success');
      onUpdated();
      setTimeout(() => {
        handleBackButton();
      }, 3000);
    } catch (err) {
      console.error('Error saving review data', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    const hasChanges = Object.keys(reviewData).length > 0;
    if (hasChanges) {
      setShowBackDialog(true);
    } else {
      handleBackButton();
    }
  };

  const handleConfirmBack = () => {
    setShowBackDialog(false);
    handleBackButton();
  };

  const handleSnackbarClose = (
    event: Event | SyntheticEvent<any, Event>,
    reason: SnackbarCloseReason
  ) => {
    if (saveStatus === 'error') {
      setSaveStatus(null);
    }
  };

  const isSaveEnabled = hasReachedLastPage && !saving && !saveStatus;

  return resultIds.length === 0 ? (
    <Navigate to={'/results'} />
  ) : (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '90vh',
          p: 2,
        }}
      >
        {/* Content: scrollable results */}
        <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto' }}>
          <Stack spacing={2}>
            {resultIds.map((resultId, index) => {
              const inWindow = index >= windowStart && index < windowEnd;
              if (!inWindow) return null;
              return (
                <Box key={resultId} sx={{ p: 1, textAlign: 'center' }}>
                  <ResultReviewItem
                    resultId={resultId}
                    value={reviewData[resultId]}
                    onChange={(updates) =>
                      handleUpdateReview(resultId, updates)
                    }
                    changeActionSelected={changeActionSelected[resultId]}
                    onChangeActionSelected={(update) =>
                      handleUpdateActionConfirm(resultId, update)
                    }
                    accessToken={authData.access}
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Footer: compact pagination and action buttons */}
        <Box
          sx={{
            pt: 2,
            paddingX: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left side: Progress indicator */}
          <Typography variant="body2" color="text.secondary">
            {reviewedCount} of {resultIds.length} results reviewed
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }}>
            {/* Pagination controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="text"
                onClick={handlePrevious}
                disabled={windowStart === 0}
                size="small"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <ArrowBackIcon fontSize="small" />
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  px: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 'medium', lineHeight: 1.2 }}
                >
                  {windowStart + 1} - {windowEnd} of {resultIds.length}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1 }}
                >
                  Page {currentPage} of {totalPages}
                </Typography>
              </Box>

              <Button
                variant="text"
                onClick={handleNext}
                disabled={windowEnd >= resultIds.length}
                size="small"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <ArrowForwardIcon fontSize="small" />
              </Button>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <RareButton
                onClick={handleBackClick}
                type="button"
                variant="outlined"
                sx={{ py: 1, px: 4 }}
              >
                Back
              </RareButton>
              <RareButton
                onClick={handleSaveClick}
                type="button"
                variant="success"
                disabled={!isSaveEnabled}
                sx={{ py: 1, px: 4 }}
              >
                {saving ? 'Saving...' : 'Save'}
              </RareButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Save Confirmation Dialog */}
      <Dialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        aria-labelledby="save-dialog-title"
        aria-describedby="save-dialog-description"
      >
        <DialogTitle id="save-dialog-title">Save Review Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="save-dialog-description">
            You are about to save all review changes for {resultIds.length}{' '}
            results. This will update the interpretation and notes for all
            reviewed items. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="primary"
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Back Confirmation Dialog */}
      <Dialog
        open={showBackDialog}
        onClose={() => setShowBackDialog(false)}
        aria-labelledby="back-dialog-title"
        aria-describedby="back-dialog-description"
      >
        <DialogTitle id="back-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="back-dialog-description">
            You have unsaved review changes that will be lost if you go back.
            Are you sure you want to continue without saving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBackDialog(false)} color="primary">
            Stay Here
          </Button>
          <Button onClick={handleConfirmBack} color="error" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {saveStatus ? (
        <Snackbar
          open={saveStatus === 'success' || saveStatus === 'error'}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          onClose={handleSnackbarClose}
        >
          <Alert
            severity={saveStatus === 'success' ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {saveStatus === 'success'
              ? 'Reviews saved successfully! Returning to results...'
              : 'An unexpected error occurred while saving the reviews.'}
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
};

export default ResultsReviewCard;
